'use client'
import React, { useState, useCallback } from 'react'
import {
  Box,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCopy,
  faCheck,
  faChevronDown,
  faChevronRight,
  faFilter,
  faRefresh,
} from '@fortawesome/pro-regular-svg-icons'
import ChildCard from 'src/components/era-package/shared/ChildCard'
import Button from "../../../../components/era-package/forms/Button";

// ─── helpers ───────────────────────────────────────────────────────────────

const fmt = (v) => (v == null || v === '' ? '-' : v)

const HeaderField = ({ label, value }) => {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', fontSize: '0.75rem', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
        {value || '-'}
      </Typography>
    </Grid>
  )
}

const InfoRow = ({ label, value, fullWidth, isHeader }) => {
  if (isHeader) {
    return <HeaderField label={label} value={value} />
  }
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={500} sx={{ display: 'block' }}>
        {value || '-'}
      </Typography>
    </Box>
  )
}

// ─── header field config per doc type ─────────────────────────────────────

// Common fields all types share
const COMMON_FIELDS = [
  { label: 'Submission Type',  key: 'submissionType' },
  { label: 'Response Type',    key: 'responseType' },
  { label: 'Submitted By',     key: 'submittedBy' },
  { label: 'Submitted Time',   key: 'submittedTime' },
  { label: 'Received Time',    key: 'receivedTime' },
]

const HEADER_FIELDS_BY_TYPE = {
  airManifest: [
    { label: 'Carrier',                      key: 'carrier' },
    { label: 'Flight',                        key: 'vesselFlight' },
    { label: 'Flight No.',                    key: 'vesselFlightNo' },
    { label: 'Arrival Date',                  key: 'arrivalDate' },
    { label: 'Manifest Amended Quantity',     key: 'manifestedAmendedQuantity' },
    { label: 'Actual Port of Unlading/Import',key: 'actualPortOfUnlading' },
    ...COMMON_FIELDS,
  ],
  oceanManifest: [
    { label: 'Carrier',                       key: 'carrier' },
    { label: 'Vessel',                        key: 'vesselFlight' },
    { label: 'Vessel/Flight No.',             key: 'vesselFlightNo' },
    { label: 'Arrival Date',                  key: 'arrivalDate' },
    { label: 'Manifest Amended Quantity',     key: 'manifestedAmendedQuantity' },
    { label: 'Actual Port of Unlading/Import',key: 'actualPortOfUnlading' },
    { label: 'Vessel Departure Port',         key: 'vesselDeparturePort' },
    { label: 'Vessel Departure Date',         key: 'vesselDepartureDate' },
    ...COMMON_FIELDS,
  ],
  railManifest: [
    { label: 'Carrier',                       key: 'carrier' },
    { label: 'Train',                         key: 'vesselFlight' },
    { label: 'Train No.',                     key: 'vesselFlightNo' },
    { label: 'Arrival Date',                  key: 'arrivalDate' },
    { label: 'Manifest Amended Quantity',     key: 'manifestedAmendedQuantity' },
    { label: 'Actual Port of Unlading/Import',key: 'actualPortOfUnlading' },
    { label: 'Departure Port',                key: 'vesselDeparturePort' },
    { label: 'Departure Date',                key: 'vesselDepartureDate' },
    ...COMMON_FIELDS,
  ],
  truckManifest: [
    { label: 'Carrier',                       key: 'carrier' },
    { label: 'Conveyance',                    key: 'vesselFlight' },
    { label: 'Trip No.',                      key: 'vesselFlightNo' },
    { label: 'Arrival Date',                  key: 'arrivalDate' },
    { label: 'Manifest Amended Quantity',     key: 'manifestedAmendedQuantity' },
    { label: 'Actual Port of Unlading/Import',key: 'actualPortOfUnlading' },
    ...COMMON_FIELDS,
  ],
  inBond: [
    { label: 'Arrival Date',                  key: 'arrivalDate' },
    { label: 'Actual Port of Unlading/Import',key: 'actualPortOfUnlading' },
    ...COMMON_FIELDS,
  ],
  importerBond: [
    ...COMMON_FIELDS,
  ],
  manufacturerMID: [
    ...COMMON_FIELDS,
  ],
}

// fallback — show all
const DEFAULT_FIELDS = [
  { label: 'Carrier',                       key: 'carrier' },
  { label: 'Vessel / Flight',               key: 'vesselFlight' },
  { label: 'Vessel / Flight No.',           key: 'vesselFlightNo' },
  { label: 'Arrival Date',                  key: 'arrivalDate' },
  { label: 'Manifest Amended Quantity',     key: 'manifestedAmendedQuantity' },
  { label: 'Actual Port of Unlading/Import',key: 'actualPortOfUnlading' },
  { label: 'Vessel Departure Port',         key: 'vesselDeparturePort' },
  { label: 'Vessel Departure Date',         key: 'vesselDepartureDate' },
  ...COMMON_FIELDS,
]

// ─── parse header fields from EDI text ─────────────────────────────────────

const parseHeaderFromText = (text) => {
  if (!text) return {}
  const line = text.split('\n').find((l) => l.trim().startsWith('Carrier:')) || ''
  const get = (pattern) => { const m = line.match(pattern); return m ? m[1].trim() : null }

  const getField = (pattern) => {
    const m = text.match(pattern)
    if (!m) return null
    const val = m[1].trim()
    return val === '' ? null : val
  }

  return {
    carrier:                   get(/Carrier:\s*(\S+)/),
    vesselFlight:              get(/Vessel\/Flight:\s*(\S+)/),
    vesselFlightNo:            get(/Voyage\/Flight\s*No[.:]\s*(\S+)/),
    arrivalDate:               get(/Arrival Date:\s*(\S+)/),
    manifestedAmendedQuantity: getField(/(?:Master|House) Manifest Amended Quantity:\s*(\S+)/),
    actualPortOfUnlading:      getField(/Actual Port of Unlading\/Import:\s*(.+?)(?:\n|$)/),
    vesselDeparturePort:       getField(/Vessel Departure Port:\s*(.+?)(?:\n|$)/),
    vesselDepartureDate:       getField(/Vessel Departure Date:\s*(\S+)/),
    masterBl:                  getField(/Master B\/L:\s*([^\s\]]+)/),
    mawbFirst:                 getField(/\[MAWB:\s*([^\s\]]+)/),
  }
}

// ─── Header summary bar ─────────────────────────────────────────────────────

const ResponseHeader = ({ data }) => {
  const parsed = React.useMemo(() => parseHeaderFromText(data?.responseText), [data?.responseText])
  if (!data) return null
  const h = { ...parsed, ...data }
  const fields = HEADER_FIELDS_BY_TYPE[h.queryType] || DEFAULT_FIELDS
  return (
    <ChildCard sx={{ mb: 2 }}>
      <Box sx={{ p: 2.5 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Box sx={{ pb: 2 }}>
              <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', fontSize: '0.75rem', mb: 0.5 }}>
                Reference Number
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1976d2' }}>
                {h.referenceNumber || h.masterBl || '-'}
              </Typography>
            </Box>
          </Grid>
          {h.queryType === 'airManifest' && h.mawbFirst && (
            <HeaderField label="Master Air Waybill" value={h.mawbFirst} />
          )}
          {fields.filter((f) => !['arrivalDate', 'submittedTime', 'receivedTime', 'vesselDepartureDate'].includes(f.key)).map((f) => (
            <HeaderField key={f.key} label={f.label} value={h[f.key]} />
          ))}
          {fields.filter((f) => ['arrivalDate', 'submittedTime', 'receivedTime', 'vesselDepartureDate'].includes(f.key)).map((f) => (
            <HeaderField key={f.key} label={f.label} value={h[f.key]} />
          ))}
        </Grid>
      </Box>
    </ChildCard>
  )
}

// ─── Raw EDI tab ────────────────────────────────────────────────────────────

const RawEdiTab = ({ responseText }) => {
  const theme = useTheme()
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    if (!responseText) return
    navigator.clipboard.writeText(responseText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [responseText])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Tooltip title={copied ? 'Copied!' : 'Copy EDI'}>
          <IconButton onClick={handleCopy} size="small" color={copied ? 'success' : 'default'}>
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} style={{ width: 16, height: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 520,
          overflowY: 'auto',
          backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
        }}
      >
        {responseText || '-'}
      </Box>
    </Box>
  )
}

// ─── Simplified tab ──────────────────────────────────────────────────────────

/**
 * Parse the raw EDI text into HWB rows.
 * The ocean response format example shows sections starting with [Master B/L: ... HAWB: ...]
 * We extract per-HWB data blocks.
 */
const parseHwbRows = (responseText, queryType) => {
  if (!responseText) return []

  const lines = responseText.split('\n')
  const rows = []
  let serialNo = 0
  let currentBlock = null
  // last seen carrier info (inherited across BOL blocks for air format)
  let lastCarrier = { carrier: null, vessel: null, vesselFlight: null, voyageFlightNo: null, arrivalDate: null }

  const pushBlock = () => {
    if (currentBlock) rows.push(currentBlock)
  }

  const newBlock = () => ({
    sn: ++serialNo,
    hawb: null, mawb: null,
    houseManifestQuantity: null, houseManifestAmendedQuantity: null, port: null, dispositionCode: null,
    ...lastCarrier,
    vesselFlight: lastCarrier.vesselFlight,
    voyageFlightNo: lastCarrier.voyageFlightNo,
    billOfLadingType: null, sfStatus: null,
    transportationMode: null, manifestedPortOfUnlading: null,
    actualPortOfUnlading: null, vesselDeparturePort: null,
    activities: [], rawLines: [],
  })

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    // Carrier header line — update lastCarrier, no new block yet
    if (line.startsWith('Carrier:')) {
      const carrierMatch = line.match(/Carrier:\s*(\S+)/)
      const vesselMatch  = line.match(/Vessel\/Flight:\s*(\S+)/)
      const voyageMatch  = line.match(/Voyage\/Flight\s*No[.:]\s*(\S+)/)
      const arrivalMatch = line.match(/Arrival Date:\s*(\S+)/)
      lastCarrier = {
        carrier:      carrierMatch  ? carrierMatch[1]  : lastCarrier.carrier,
        vessel:       vesselMatch   ? vesselMatch[1]   : lastCarrier.vessel,
        vesselFlight: vesselMatch   ? vesselMatch[1]   : lastCarrier.vesselFlight,
        voyageFlightNo: voyageMatch ? voyageMatch[1]   : lastCarrier.voyageFlightNo,
        arrivalDate:  arrivalMatch  ? arrivalMatch[1]  : lastCarrier.arrivalDate,
      }
      continue
    }

        // BOL identifier line — create block for MAWB or Master B/L
    if (line.includes('[Master B/L:') || line.match(/^\[MAWB:/)) {
      const mawbMatch = line.match(/(?:Master B\/L|MAWB):\s*([^\s\]]+)/)
      const hawbMatch = line.match(/HAWB:\s*([^\s\]]+)/)
      // For air: include both MAWB-only and MAWB+HAWB. For ocean: create if MAWB exists
      if (!mawbMatch) continue
      pushBlock()
      currentBlock = newBlock()
      if (mawbMatch) currentBlock.mawb = mawbMatch[1]
      if (hawbMatch) currentBlock.hawb = hawbMatch[1]
      continue
    }

    if (!currentBlock) continue
    currentBlock.rawLines.push(line)

    // Detail fields
    if (line.startsWith('House Manifest Quantity:')) {
      const m = line.match(/House Manifest Quantity:\s*(\d+)/)
      if (m) currentBlock.houseManifestQuantity = m[1]
    } else if (line.startsWith('Manifest Quantity:')) {
      const m = line.match(/Manifest Quantity:\s*(\d+)/)
      if (m) currentBlock.houseManifestQuantity = m[1]
    } else if (line.startsWith('House Manifest Amended Quantity:')) {
      const m = line.match(/House Manifest Amended Quantity:\s*(\d+)/)
      if (m) currentBlock.houseManifestAmendedQuantity = m[1]
    } else if (line.startsWith('Bill of Lading Type:')) {
      const m = line.match(/Bill of Lading Type:\s*(.+)/)
      if (m) currentBlock.billOfLadingType = m[1].trim()
    } else if (line.startsWith('SF Status:')) {
      const m = line.match(/SF Status:\s*(.+)/)
      if (m) currentBlock.sfStatus = m[1].trim()
    } else if (line.startsWith('Transportation Mode:')) {
      const m = line.match(/Transportation Mode:\s*(.+)/)
      if (m) currentBlock.transportationMode = m[1].trim()
    } else if (line.startsWith('Manifested Port of Unlading/Import:')) {
      const m = line.match(/Manifested Port of Unlading\/Import:\s*(.+)/)
      if (m) currentBlock.manifestedPortOfUnlading = m[1].trim()
    } else if (line.startsWith('Actual Port of Unlading/Import:')) {
      const m = line.match(/Actual Port of Unlading\/Import:\s*(.+)/)
      if (m) { currentBlock.actualPortOfUnlading = m[1].trim(); currentBlock.port = m[1].trim() }
    } else if (line.startsWith('Vessel Departure Port:')) {
      const m = line.match(/Vessel Departure Port:\s*(.+)/)
      if (m) currentBlock.vesselDeparturePort = m[1].trim()
    }

    // Activity lines: [MM/DD/YY HH:MM] or [MM/DD/YY HH:MM:SS] CODE: DESCRIPTION
    const actMatch = line.match(/^\[(\d{2}\/\d{2}\/\d{2})\s+(\d{2}:\d{2}(?::\d{2})?)\]\s+([^:]+):\s*(.*?)(?:\s+Quantity:\s*(.*))?$/)
    if (actMatch) {
      const [, date, time, code, desc, qty] = actMatch
      currentBlock.activities.push({
        date,
        time,
        code: code.trim(),
        description: desc.trim(),
        quantity: qty?.trim() ?? '',
        index: currentBlock.activities.length,
      })
      // Take last activity code as dispositionCode
      currentBlock.dispositionCode = code.trim()
    }
    }
  pushBlock()

  // Calculate HAWB count per MAWB for MAWB-only rows
  const hawbCountByMawb = {}
  rows.forEach((r) => {
    if (r.mawb && r.hawb) {
      if (!hawbCountByMawb[r.mawb]) hawbCountByMawb[r.mawb] = 0
      hawbCountByMawb[r.mawb]++
    }
  })

  // Set hawb count for MAWB-only rows
  rows.forEach((r) => {
    if (r.mawb && !r.hawb && hawbCountByMawb[r.mawb]) {
      r.houseManifestQuantity = String(hawbCountByMawb[r.mawb])
    }
  })

  // sort activities latest first, then by reverse original order for same time
  rows.forEach((r) => {
    r.activities.sort((a, b) => {
      const toMs = ({ date, time }) => {
        const [mm, dd, yy] = (date || '').split('/')
        const t = (time || '00:00').substring(0, 5)
        return new Date(`20${yy}-${mm}-${dd}T${t}`).getTime()
      }
      const timeA = toMs(a)
      const timeB = toMs(b)
      if (timeA !== timeB) return timeB - timeA
      return b.index - a.index
    })
  })

  return rows
}

// ─── HWB Row (expandable) ────────────────────────────────────────────────────

const detail1Fields = [
  { key: 'vesselFlight', label: 'Vessel / Flight' },
  { key: 'voyageFlightNo', label: 'Voyage / Flight No.' },
  { key: 'houseManifestAmendedQuantity', label: 'House Manifest Amended Quantity' },
  { key: 'billOfLadingType', label: 'Bill of Lading Type' },
  { key: 'sfStatus', label: 'SF Status' },
  { key: 'transportationMode', label: 'Transportation Mode' },
  { key: 'manifestedPortOfUnlading', label: 'Manifested Port of Unlading/Import' },
  { key: 'actualPortOfUnlading', label: 'Actual Port of Unlading/Import' },
  { key: 'vesselDeparturePort', label: 'Vessel Departure Port' },
]

const HwbExpandRow = ({ row, displayIndex, colSpan, visibleHeaders }) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)

  const availableDetails = detail1Fields.filter((f) => row[f.key] != null && row[f.key] !== '')
  
  const hasMawb = visibleHeaders?.some((h) => h.id === 'mawb')
  const hasHawb = visibleHeaders?.some((h) => h.id === 'hawb')

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer' }}
        onClick={() => setOpen((p) => !p)}
      >
        <TableCell>
          <IconButton size="small">
            <FontAwesomeIcon
              icon={open ? faChevronDown : faChevronRight}
              style={{ width: 12, height: 12 }}
            />
          </IconButton>
        </TableCell>
        <TableCell>{displayIndex}</TableCell>
        {hasMawb && <TableCell>{fmt(row.mawb)}</TableCell>}
        {hasHawb && <TableCell>{fmt(row.hawb)}</TableCell>}
        <TableCell>{fmt(row.houseManifestQuantity)}</TableCell>
        <TableCell>{fmt(row.port)}</TableCell>
        <TableCell>
          {row.dispositionCode ? (
            <Chip label={row.dispositionCode} size="small" variant="outlined" color="primary" />
          ) : '-'}
        </TableCell>
        <TableCell>{fmt(row.arrivalDate)}</TableCell>
        <TableCell>{fmt(row.carrier)}</TableCell>
        <TableCell>{fmt(row.vessel)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0, border: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {/* Box 1 – Bill Details */}
              {availableDetails.length > 0 && (
                <Box
                  sx={{
                    flex: '1 1 340px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="caption" fontWeight={600} color="primary" sx={{ fontSize: '0.85rem' }}>
                      Bill Details
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, maxHeight: 240, overflowY: 'auto' }}>
                    {/* Detail Rows */}
                    {availableDetails.map((f, idx) => (
                      <Box key={f.key} sx={{ display: 'flex', px: 1.5, py: 0.75, borderBottom: idx < availableDetails.length - 1 ? `1px solid ${theme.palette.divider}` : 'none', gap: 0, alignItems: 'center' }}>
                        <Box sx={{ flex: '1', borderRight: `1px solid ${theme.palette.divider}`, pr: 1 }}>
                          <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.7rem' }}>
                            {f.label}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: '1', pl: 1 }}>
                          <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.7rem' }}>
                            {row[f.key] || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Box 2 – Recent Activity */}
              {row.activities.length > 0 && (
                <Box
                  sx={{
                    flex: '1 1 340px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="caption" fontWeight={600} color="primary" sx={{ fontSize: '0.85rem' }}>
                      Recent Activity
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, maxHeight: 240, overflowY: 'auto' }}>
                    {/* Header Row */}
                    <Box sx={{ display: 'flex', px: 1.5, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`, gap: 0 }}>
                      <Box sx={{ flex: '0 0 70px', borderRight: `1px solid ${theme.palette.divider}`, pr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'center', fontSize: '0.7rem' }}>
                          Code
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, borderRight: `1px solid ${theme.palette.divider}`, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'center', fontSize: '0.7rem' }}>
                          Description
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '0 0 120px', pl: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'center', fontSize: '0.7rem' }}>
                          Date/Time
                        </Typography>
                      </Box>
                    </Box>
                    {/* Activity Rows */}
                    {row.activities.map((a, idx) => {
                      const [mm, dd, yy] = (a.date || '').split('/')
                      const fmtDate = mm && dd && yy ? `${mm}/${dd}/20${yy}` : a.date
                      const fmtTime = (a.time || '').substring(0, 8)
                      return (
                        <Box key={idx} sx={{ display: 'flex', px: 1.5, py: 0.75, borderBottom: idx < row.activities.length - 1 ? `1px solid ${theme.palette.divider}` : 'none', gap: 0, alignItems: 'center' }}>
                          <Box sx={{ flex: '0 0 70px', borderRight: `1px solid ${theme.palette.divider}`, pr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap', textAlign: 'center', fontSize: '0.7rem' }}>
                              {a.code}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, borderRight: `1px solid ${theme.palette.divider}`, px: 1 }}>
                            <Typography variant="caption" fontWeight={500} sx={{ display: 'block', fontSize: '0.7rem' }}>
                              {a.description}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: '0 0 120px', pl: 1 }}>
                            <Typography variant="caption" sx={{ textAlign: 'right', display: 'block', opacity: 0.7, fontSize: '0.7rem' }}>
                              {fmtDate} {fmtTime}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

// ─── Simplified tab ──────────────────────────────────────────────────────────

const simplifiedHeadersWithoutHawb = [
  { id: 'expand', label: '', width: '35px' },
  { id: 'sn', label: 'S/N', width: '35px' },
  { id: 'mawb', label: 'MAWB', width: '130px' },
  { id: 'houseManifestQuantity', label: 'Total No. of HAWB', width: '130px' },
  { id: 'port', label: 'Port', width: '130px' },
  { id: 'dispositionCode', label: 'Disposition Code', width: '130px' },
  { id: 'arrivalDate', label: 'Arrival Date', width: '130px' },
  { id: 'carrier', label: 'Carrier', width: '130px' },
  { id: 'vessel', label: 'Vessel', width: '130px' },
]

const simplifiedHeadersWithHawb = [
  { id: 'expand', label: '', width: '35px' },
  { id: 'sn', label: 'S/N', width: '35px' },
  { id: 'hawb', label: 'HAWB', width: '130px' },
  { id: 'houseManifestQuantity', label: 'House Manifest Quantity', width: '130px' },
  { id: 'port', label: 'Port', width: '130px' },
  { id: 'dispositionCode', label: 'Disposition Code', width: '130px' },
  { id: 'arrivalDate', label: 'Arrival Date', width: '130px' },
  { id: 'carrier', label: 'Carrier', width: '130px' },
  { id: 'vessel', label: 'Vessel', width: '130px' },
]

const ReferenceTable = ({ rows, headers, filterValues, onFilterChange, showFilters, title }) => (
  <Box sx={{ mb: 3 }}>
    {rows.length > 0 && (
      <>
        {title && (
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {title}
          </Typography>
        )}
                <TableContainer sx={{ maxHeight: 560, overflowY: 'auto' }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {headers.map((h) => (
                  <TableCell key={h.id} sx={{ width: h.width }}>
                    <Typography variant="caption" fontWeight={600} color="primary.main">
                      {h.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
              {showFilters && (
                <TableRow>
                  {headers.map((h) => (
                    <TableCell key={`f-${h.id}`} sx={{ py: 0.5 }}>
                      {h.id !== 'expand' && (
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={filterValues[h.id] || ''}
                          onChange={(e) => onFilterChange(h.id, e.target.value)}
                          sx={{ minWidth: 80 }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <HwbExpandRow key={row.sn} row={row} displayIndex={idx + 1} colSpan={headers.length} visibleHeaders={headers} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    )}
  </Box>
)

const SimplifiedTab = ({ responseText, queryType }) => {
  const [filterValuesWithHawb, setFilterValuesWithHawb] = useState({})
  const [filterValuesWithoutHawb, setFilterValuesWithoutHawb] = useState({})
  const [showFilters, setShowFilters] = useState(false)

  const allRows = React.useMemo(() => parseHwbRows(responseText, queryType), [responseText, queryType])
  
  const { withHawb, withoutHawb } = React.useMemo(() => {
    const with_ = allRows.filter((r) => r.hawb != null && r.hawb !== '')
    const without_ = allRows.filter((r) => !r.hawb || r.hawb === '')
    return { withHawb: with_, withoutHawb: without_ }
  }, [allRows])

  const headersWithHawb = React.useMemo(() => simplifiedHeadersWithHawb, [])
  const headersWithoutHawb = React.useMemo(() => simplifiedHeadersWithoutHawb, [])

  const filteredWithHawb = React.useMemo(() => {
    return withHawb.filter((row) => {
      return Object.entries(filterValuesWithHawb).every(([col, val]) => {
        if (!val) return true
        const cell = row[col]
        return cell != null && String(cell).toLowerCase().includes(val.toLowerCase())
      })
    })
  }, [withHawb, filterValuesWithHawb])

  const filteredWithoutHawb = React.useMemo(() => {
    return withoutHawb.filter((row) => {
      return Object.entries(filterValuesWithoutHawb).every(([col, val]) => {
        if (!val) return true
        const cell = row[col]
        return cell != null && String(cell).toLowerCase().includes(val.toLowerCase())
      })
    })
  }, [withoutHawb, filterValuesWithoutHawb])

  const handleFilterChange = (section, colId, value) => {
    if (section === 'withHawb') {
      setFilterValuesWithHawb((prev) => ({ ...prev, [colId]: value }))
    } else {
      setFilterValuesWithoutHawb((prev) => ({ ...prev, [colId]: value }))
    }
  }

  if (allRows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No HWB data found in the response.
      </Typography>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton
          size="small"
          onClick={() => setShowFilters((p) => !p)}
          title={showFilters ? 'Hide Filters' : 'Show Filters'}
        >
          <FontAwesomeIcon icon={faFilter} style={{ width: 14, height: 14 }} />
        </IconButton>
      </Box>

            {withoutHawb.length > 0 && (
        <ReferenceTable
          rows={filteredWithoutHawb}
          headers={headersWithoutHawb}
          filterValues={filterValuesWithoutHawb}
          onFilterChange={(colId, val) => handleFilterChange('withoutHawb', colId, val)}
          showFilters={showFilters}
        />
      )}

      {withHawb.length > 0 && (
        <ReferenceTable
          rows={filteredWithHawb}
          headers={headersWithHawb}
          filterValues={filterValuesWithHawb}
          onFilterChange={(colId, val) => handleFilterChange('withHawb', colId, val)}
          showFilters={showFilters}
        />
      )}
    </Box>
  )
}

// ─── Main ResponseTab ────────────────────────────────────────────────────────

const ResponseTab = ({ data, onRefresh, isRefreshing }) => {
  const [subTab, setSubTab] = useState('rawEdi')
  const isAirOrOcean = ['airManifest', 'oceanManifest', 'railManifest', 'truckManifest'].includes(data?.queryType)

  if (!data) return (
    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
      No response data available.
    </Typography>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ResponseHeader data={data} />
      <ChildCard sx={{ p: 0 }}>
        <TabContext value={subTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
            <TabList onChange={(_, v) => setSubTab(v)} aria-label="Response sub-tabs">
              <Tab label="Raw EDI" value="rawEdi" />
              {isAirOrOcean && <Tab label="Simplified" value="simplified" />}
            </TabList>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={onRefresh}
              disabled={isRefreshing}
              startIcon={
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ width: 14, height: 14 }}
                  spin={isRefreshing}
                />
              }
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          <TabPanel value="rawEdi" sx={{ p: 2, pt: 3 }}>
            <RawEdiTab responseText={data.responseText} />
          </TabPanel>
          {isAirOrOcean && (
            <TabPanel value="simplified" sx={{ p: 2 }}>
              <SimplifiedTab responseText={data.responseText} queryType={data.queryType} />
            </TabPanel>
          )}
        </TabContext>
      </ChildCard>
    </Box>
  )
}

export default ResponseTab

